import { generateSimulationQuery, generateExposureQuery } from "modules/smart-trade-rule-search/helpers"
import { ContentSetWithEscapedId, Classification, PositionSetInput, ModelMetadata } from "./types"

// GraphQL aliases for simulation queries
export const SIMULATION_ALIASES = {
  MAIN: "simulation",
  ACTIVE: "activeSimulation",
} as const

export interface GraphQLQuery {
  query: string
  variables: {
    modelId: string
    positionSet: PositionSetInput
    base?: PositionSetInput
    on: string
  }
}

export function cleanPositionSet(positionSet: PositionSetInput): PositionSetInput {
  return {
    type: positionSet.type,
    id: positionSet.id,
    ...(positionSet.experimentId !== undefined && { experimentId: positionSet.experimentId }),
    ...(positionSet.weight !== undefined && { weight: positionSet.weight }),
    ...(positionSet.dates !== undefined && { dates: positionSet.dates }),
    ...(positionSet.segment !== undefined && { segment: positionSet.segment }),
    ...(positionSet.expand !== undefined && { expand: positionSet.expand }),
    ...(positionSet.fxHedgeEnabled !== undefined && { fxHedgeEnabled: positionSet.fxHedgeEnabled }),
  }
}

function buildSimulationFragment(
  simulationQueryPart: string,
  exposureQueryPart: string,
  hasBase: boolean,
  alias?: string,
): string {
  const aliasPrefix = alias ? `${alias}: ` : ""
  return `
    ${aliasPrefix}simulation(positionSet: $positionSet, from: $on, to: $on${hasBase ? ", base: $base" : ""}) {
      ${simulationQueryPart}
      ${exposureQueryPart}
    }`
}

export function buildRulesQuery(
  modelId: string | null,
  endDate: string,
  positionSet: PositionSetInput,
  contentSets: ContentSetWithEscapedId[] | null,
  classifications: Classification[] | null,
  modelMetadata: ModelMetadata | null | undefined,
  basePositionSet?: PositionSetInput | null,
): GraphQLQuery | null {
  if (!modelId || !endDate || !positionSet || !contentSets || !classifications) {
    return null
  }

  const missingDataFiles = modelMetadata?.missingDataFiles || []
  const cleanedPositionSet = cleanPositionSet(positionSet)
  const simulationQueryPart = generateSimulationQuery(classifications)
  const exposureQueryPart = generateExposureQuery(contentSets, missingDataFiles)

  // Build query with optional base simulation
  const hasBase = Boolean(basePositionSet)
  const cleanedBasePositionSet = basePositionSet ? cleanPositionSet(basePositionSet) : undefined

  const mainSimulation = buildSimulationFragment(simulationQueryPart, exposureQueryPart, false)
  const baseSimulation = hasBase
    ? buildSimulationFragment(simulationQueryPart, exposureQueryPart, true, SIMULATION_ALIASES.ACTIVE)
    : ""

  const queryParams = [
    "$modelId: String!",
    "$positionSet: PositionSetInput!",
    ...(hasBase ? ["$base: [PositionSetInput]"] : []),
    "$on: Date!",
  ].join(", ")

  const query = `#optimize-rules
  query (${queryParams}) {
    model(id: $modelId) {
      ${mainSimulation}
      ${baseSimulation}
    }
  }`

  return {
    query,
    variables: {
      modelId,
      positionSet: cleanedPositionSet,
      ...(hasBase && cleanedBasePositionSet && { base: cleanedBasePositionSet }),
      on: endDate,
    },
  }
}

export function validateQueryParams(
  modelId: string | null,
  endDate: string,
  positionSet: PositionSetInput | null | undefined,
): boolean {
  return Boolean(
    modelId
    && typeof modelId === "string"
    && endDate
    && typeof endDate === "string"
    && positionSet
    && typeof positionSet === "object",
  )
}
